import { sceneBgColor } from '@/editor/constant/color';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import * as THREE from 'three';
import { PDFPreviewRuler } from './ruler';
import { calMousePosition } from '@/editor/utils';
import iconMove from '@/assets/icons/cursor/move.svg?url';
import { disposeObject } from '@/editor/entities/utils';

type IIntersectDataType = 'start' | 'end' | 'body';

interface IIntersectData {
  type: IIntersectDataType;
  intersectable: boolean;
}

export class PDFFloorPlanPreviewScene {
  private _renderer: THREE.WebGLRenderer;
  private _scene: THREE.Scene;
  private _camera: THREE.OrthographicCamera;
  private _canvasElement: HTMLCanvasElement;
  private _controls: OrbitControls;
  private _pdfMesh: THREE.Mesh | null = null;
  private _ruler: PDFPreviewRuler | null = null;
  private _measureMode: 'factor' | 'ruler' = 'ruler';
  private _raycaster: THREE.Raycaster = new THREE.Raycaster();
  private _currentPress: IIntersectDataType | null = null;
  private _canvasWidth: number = 0;
  private _previousPosition: { x: number; y: number } = { x: 0, y: 0 };

  constructor() {
    const { renderer, scene, camera } = this._initScene();
    this._renderer = renderer;
    this._scene = scene;
    this._camera = camera;
    this._canvasElement = this._renderer.domElement;
    this._controls = this._initControls(this._camera, this._canvasElement);
    window.addEventListener('resize', this.handleResize);
    this._canvasElement.addEventListener('mousedown', this.handleMouseDown);
    this._canvasElement.addEventListener('mouseup', this.handleMouseUp);
    this._canvasElement.addEventListener('mousemove', this.handleMouseMove);
    this._controls.addEventListener('change', this.handleChanges);
    this._renderScene();
    this.changeMeasureMode(this._measureMode);
  }

  get measureMode() {
    return this._measureMode;
  }

  get handleResize() {
    return this._handleResize.bind(this);
  }

  get handleMouseDown() {
    return this._handleMouseDown.bind(this);
  }

  get handleMouseUp() {
    return this._handleMouseUp.bind(this);
  }

  get handleMouseMove() {
    return this._handleMouseMove.bind(this);
  }

  get handleChanges() {
    return this._handleChanges.bind(this);
  }

  get controls() {
    return this._controls;
  }

  get scene() {
    return this._scene;
  }

  get renderer() {
    return this._renderer;
  }

  get camera() {
    return this._camera;
  }

  get canvasElement() {
    return this._canvasElement;
  }

  get canvasContainer() {
    return this._renderer.domElement.parentElement;
  }

  setMeasureMode(mode: 'ruler' | 'factor') {
    this.changeMeasureMode(mode);
  }

  getRatio() {
    if (!this._ruler) return 1;
    const distance = this._ruler.distance;
    return distance / this._canvasWidth;
  }

  getInputValue() {
    if (!this._ruler) return 1;
    return this._ruler.value;
  }

  changeMeasureMode(mode: 'ruler' | 'factor') {
    this._measureMode = mode;
    if (mode === 'ruler') {
      if (!this._ruler) {
        this._createRuler();
      } else {
        this._ruler.setVisible(true);
      }
    } else {
      if (this._ruler) {
        this._ruler.setVisible(false);
      }
    }
  }

  private _createRuler() {
    this._ruler = new PDFPreviewRuler(this._camera, this._renderer);
    this._ruler.setCameraScale(this._camera.zoom);
    this._scene.add(this._ruler.model);
  }

  addCanvasMesh(canvas: HTMLCanvasElement) {
    const texture = new THREE.CanvasTexture(canvas);
    const aspect = canvas.width / canvas.height;
    const height = canvas.height / 100;
    const width = height * aspect;
    this._canvasWidth = width;
    const geometry = new THREE.PlaneGeometry(width, height);
    const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geometry, material);
    this._pdfMesh = mesh;
    this._scene.add(mesh);
    this.fitToView();
    this.handleChanges();
  }

  fitToView() {
    if (!this._pdfMesh) return;

    const bbox = new THREE.Box3().setFromObject(this._pdfMesh);
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    this._ruler?.updateStartPoint({
      x: center.x - size.x / 4,
      y: center.y
    });
    this._ruler?.updateEndPoint({
      x: center.x + size.x / 4,
      y: center.y
    });
    const padding = Math.max(size.x, size.y) * 0.025;
    this._camera.position.set(center.x, center.y, this._camera.position.z);
    this._controls.target.set(center.x, center.y, 0);
    const zoomLv = Math.min(
      (this._camera.top - this._camera.bottom) / (size.y + padding),
      (this._camera.right - this._camera.left) / (size.x + padding),
      1
    );
    this._camera.zoom = zoomLv;
    this._controls.minZoom = zoomLv;
    this._camera.updateProjectionMatrix();
    this._controls.update();
  }

  private _handleResize() {
    const sceneContainer = this._renderer.domElement.parentElement;
    if (!sceneContainer) return;

    const width = sceneContainer.clientWidth;
    const height = sceneContainer.clientHeight;

    this._renderer.setSize(width, height);

    const camera = this._camera;

    // keep aspect ratio
    const ratio = width / height;
    const frustumHeight = camera.top - camera.bottom;
    const frustumWidth = frustumHeight * ratio;

    camera.left = -frustumWidth / 2;
    camera.right = frustumWidth / 2;
    camera.top = frustumHeight / 2;
    camera.bottom = -frustumHeight / 2;
    camera.updateProjectionMatrix();
    this._controls.update();
    this._ruler?.setParentElement(sceneContainer);
  }

  private _handleMouseDown(event: MouseEvent) {
    const position = calMousePosition(event, this._canvasElement);
    const intersectedObjects = this._getIntersectedObjects(position);
    if (intersectedObjects.length > 0) {
      const intersectData = intersectedObjects[0];
      this._currentPress = intersectData.type;
      this._previousPosition = this._pagePositionToScenePosition(calMousePosition(event, this._canvasElement));
    } else {
      this._currentPress = null;
      this._previousPosition = { x: 0, y: 0 };
    }
  }
  private _handleMouseUp(_event: MouseEvent) {
    this._currentPress = null;
  }

  private _setCursor(state: 'auto' | 'move') {
    switch (state) {
      case 'move': {
        this._canvasElement.style.cursor = `url(${iconMove}) 12 12, auto`;
        break;
      }
      case 'auto':
      default: {
        this._canvasElement.style.cursor = 'auto';
        break;
      }
    }
  }

  private _handleMouseMove(event: MouseEvent) {
    if (!this._ruler) return;

    const mousePosition = calMousePosition(event, this._canvasElement);
    if (Math.abs(mousePosition.x) > 1) {
      mousePosition.x = mousePosition.x > 0 ? 1 : -1;
    }

    if (Math.abs(mousePosition.y) > 1) {
      mousePosition.y = mousePosition.y > 0 ? 1 : -1;
    }

    if (!this._currentPress) {
      const intersectedObjects = this._getIntersectedObjects(mousePosition);
      if (intersectedObjects.length > 0) {
        this._setCursor('move');
      } else {
        this._setCursor('auto');
      }
      return;
    }

    const position = this._pagePositionToScenePosition(mousePosition);
    switch (this._currentPress) {
      case 'start': {
        this._ruler.updateStartPoint(position);
        break;
      }
      case 'end': {
        this._ruler.updateEndPoint(position);
        break;
      }
      case 'body': {
        this._ruler.moveBody(this._previousPosition, position);
        this._previousPosition = position;
        break;
      }
      default: {
        //
      }
    }
  }

  private _renderScene() {
    const animate = () => {
      requestAnimationFrame(animate);
      this._renderer.render(this._scene, this._camera);
    };
    animate();
  }

  private _handleChanges() {
    if (this._measureMode === 'ruler' && this._ruler) {
      this._ruler.setCameraScale(this._camera.zoom);
    }
  }

  private _initScene() {
    const size = 10;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-size, size, size, -size, 0.1, 1000);

    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(sceneBgColor, 1); // 设置背景颜色
    renderer.setPixelRatio(window.devicePixelRatio); // 设置像素比
    renderer.shadowMap.enabled = true; // 启用阴影
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // 设置阴影类型

    camera.position.set(0, 0, 5); // 设置相机位置
    camera.lookAt(0, 0, 0); // 设置相机朝向
    camera.layers.enableAll();
    return {
      renderer,
      scene,
      camera
    };
  }

  private _initControls(camera: THREE.OrthographicCamera, canvas: HTMLCanvasElement) {
    const controls = new OrbitControls(camera, canvas);

    controls.mouseButtons = {
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.PAN
    };

    controls.enableRotate = false; // 禁止旋转
    controls.screenSpacePanning = true; // 平移方式
    controls.zoomToCursor = true;

    return controls;
  }

  private _getIntersectedObjects(mousePosition: THREE.Vector2) {
    this._raycaster.setFromCamera(mousePosition, this._camera);
    const intersects = this._raycaster.intersectObject(this._scene, true);
    if (intersects.length > 0) {
      return intersects
        .map((item) => {
          let parent = item.object;
          while (parent.parent) {
            if (parent.userData.intersectable) {
              return parent.userData as IIntersectData;
            }
            parent = parent.parent;
          }
          return null;
        })
        .filter((item) => item !== null);
    }
    return [];
  }

  private _pagePositionToScenePosition(point: THREE.Vector2) {
    const worldPos = new THREE.Vector3(point.x, point.y, 0);
    worldPos.unproject(this._camera);
    worldPos.z = 0;

    return { x: worldPos.x, y: worldPos.y };
  }

  dispose() {
    window.removeEventListener('resize', this.handleResize);
    this._canvasElement.removeEventListener('mousedown', this.handleMouseDown);
    this._canvasElement.removeEventListener('mouseup', this.handleMouseUp);
    this._canvasElement.removeEventListener('mousemove', this.handleMouseMove);
    this._controls.removeEventListener('change', this.handleChanges);
    if (this._pdfMesh) {
      if (this._pdfMesh.geometry) {
        this._pdfMesh.geometry.dispose();
      }

      if (this._pdfMesh.material) {
        const materials = Array.isArray(this._pdfMesh.material) ? this._pdfMesh.material : [this._pdfMesh.material];

        materials.forEach((mat) => {
          if (mat instanceof THREE.MeshBasicMaterial && mat.map) {
            mat.map.dispose();

            if (mat.map.image) {
              mat.map.image.width = 0;
              mat.map.image.height = 0;
              mat.map.image = null;
            }
          }
          mat.dispose();
        });
      }
    }

    this._scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        disposeObject(object);
      }
    });

    if (this._renderer.domElement && this._renderer.domElement.parentNode) {
      this._renderer.domElement.parentNode.removeChild(this._renderer.domElement);
    }
    this._renderer.dispose();
    this._controls.dispose();
    this._ruler?.dispose();
    this._scene.clear();
    this._pdfMesh = null;
  }
}
